import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useValueChurnProbe } from "../utils/loopDebug";
import { useCalendar } from "./CalendarContext";
import moment from "moment";

const CalendarEventContext = createContext(null);

export function CalendarEventProvider({ children }) {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedAction, setSelectedAction] = useState(
    localStorage.getItem("selectedAction") || "appointment",
  ); // Should only be appointment, walk-in, or block
  // Persisted across CalendarView remounts so a token refresh doesn't clear
  // the deduplication guard and trigger a replaceAll re-fetch.
  const [fetchedMonths, setFetchedMonths] = useState(new Set());

  const selectAndStoreAction = useCallback((action) => {
    setSelectedAction(action);
    localStorage.setItem("selectedAction", action);
  }, []);

  // [LOOP] churn probe
  useValueChurnProbe("CalendarEventContext", { events, selectedEvent });

  // Memoized so consumers don't re-render on every provider render; all
  // members are state, useState setters, or the useCallback'd action setter.
  const value = useMemo(
    () => ({
      selectedEvent,
      setSelectedEvent,
      selectedAction,
      setSelectedAction: selectAndStoreAction,
      events,
      setEvents,
      fetchedMonths,
      setFetchedMonths,
    }),
    [
      selectedEvent,
      selectedAction,
      selectAndStoreAction,
      events,
      fetchedMonths,
    ],
  );

  return (
    <CalendarEventContext.Provider value={value}>
      {children}
    </CalendarEventContext.Provider>
  );
}

export const useCalendarEvent = () => {
  const context = useContext(CalendarEventContext);
  if (!context) {
    throw new Error(
      "useCalendarEvent must be used within a CalendarEventProvider",
    );
  }
  return context;
};
